import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import dayjs from 'dayjs';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Checkbox from '@mui/material/Checkbox';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import * as mime from 'react-native-mime-types';
import { FilePlus, Info, Search, X } from 'lucide-react';
import { Card, Box, Stack, Skeleton, Select, MenuItem, Radio, TextField, IconButton, FormControl, Tooltip } from "@mui/material";
import { Input, TextArea } from '@CommonFunctionalDisplay/InputBox';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useInView } from 'react-intersection-observer';
import useParamHook from '@Hooks/useParamHook';
import useDebounce from '@Hooks/useDebounce';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxFileStorage } from '@Hooks/reduxFileStorageHook/useReduxFileStorage';
import { useReduxTemplateFiles } from '@Hooks/reduxTemplateFilesHook/useReduxTemplateFiles';
import { useFileUploadHook } from '@Hooks/useFileUploadHook';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import CampaignRunService from '@Helpers/URLHelpers/WeboutService/CampaignRunService';
import CampaignRunViewService from '@Helpers/URLHelpers/CampaignRunViewService';
import WhatsappTemplatePreview from '@CommonRepresentationalDisplay/WhatsappTemplatePreview';
import WhatsappSenderTemplatePreview from '@CommonRepresentationalDisplay/WhatsappSenderTemplatePreview';
import KaiziqLeadsConnectionService from '@Helpers/URLHelpers/WeboutService/KaiziqLeadsConnectionService';
import RegistryLinkService from "@Helpers/URLHelpers/WeboutService/RegistryLinkService";
import ErrorHandler from '@Root/ErrorHandler';
import "./CampaignForm.scss";

const CampaignForm = (props) => {

    const campaignId = useParamHook('campaignId');
    const { currentLeadsId, templateList, numbersList, setTemplateList, setNumbersList, currentPhoneNumber } = useReduxLeads();
    const { currentProjectId } = useReduxProject();
    const { activeUserId } = useReduxActiveUser();
    const { fetchTemplateFileList, templateFiles, addNewFileForTemplate } = useReduxTemplateFiles();
    const { validate, fileType, dimensions } = useFileUploadHook();
    const { fileStorage, fetchFileStorage, filesCount, currentFiles } = useReduxFileStorage();

    const [removedIdList, setRemovedIdList] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const [cursor, setCursor] = useState('auto');
    const [campaignName, setCampaignName] = useState('');
    const [variableType, setVariableType] = useState('static');
    const [headerType, setHeaderType] = useState('');
    const [template, setTemplate] = useState(null);
    const [bodyText, setBodyText] = useState('');
    const [variableData, setVariableData] = useState({
        header: {},
        body: {}
    });
    const [autoRetries, setAutoRetries] = useState(true);
    const [retryCount, setRetryCount] = useState(3);
    const [scheduleTime, setScheduleTime] = useState(null);
    const [selectedItems, setSelectedItems] = useState({
        labelCard: new Set(),
        nameCard: new Set()
    });
    const [templateUse, setTemplateUse] = useState({});
    const [selectAllContact, setSelectAllContact] = useState(false);
    const [isScheduled, setIsScheduled] = useState(false);
    const [changeFile, setChangeFile] = useState(false);
    const [header, setHeader] = useState({format: '', str: ''});
    const [phoneNumberId, setPhoneNumberId] = useState();
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [step, setStep] = useState(0);

    const [allNameCardList, setAllNameCardList] = useState([]);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500, true);
    const [currentView, setCurrentView] = useState('registry');
    const [tabValue, setTabValue] = useState('labelCard');
    const [labelsList, setLabelsList] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loadMorePage, setLoadMorePage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [selectAllLabelList, setSelectAllLabelList] = useState([]);

    const campaignRunService = new CampaignRunService();
    const campaignRunViewService = new CampaignRunViewService();
    const kaiziqLeadsConnectionService = new KaiziqLeadsConnectionService();
    const registryLinkService = new RegistryLinkService();
    const errorHandler = new ErrorHandler();
    const scrollRef = useRef(null);
    const [ref, inView] = useInView({
        triggerOnce: false,
        rootMargin: '100px'
    });
    const dataCache = useRef({});
    const nameCardMapRef = useRef(new Map());

    const [whatsappFile, setWhatsappFile] = useState();
    const [files, setFiles] = useState(null);
    const inputRef = useRef();

    const navigate = useNavigate();
    const location = useLocation();

    const className = 'campaignForm';

    const isUpdate = location.pathname.endsWith('/update');

    useEffect(() => {
        renderData();
    }, []);

    useEffect(() => {
        fetchLabels(tabValue, page);
    }, [tabValue, page]);

    useEffect(() => {
        if (inView && hasMore && !loading && !loadingMore) {
            loadMoreLabels();
        }
    }, [inView]);

    useEffect(() => {
        refetchData();
    }, [debouncedSearch]);

    useEffect(() => {
        setLoadMorePage(1);
        setHasMore(true);
        setSelectAllLabelList([]);
    }, [debouncedSearch]);

    const renderData = async () => {
        setLoading(true);
        if(!!props.itemType) {
            if(!!campaignId) {
                await fetchCampaignRun();
            } else {
                if(props.itemType === 'whatsapp') {
                    renderWhatsappInitialData(currentPhoneNumber?.id);
                }
            }
        }
        setLoading(false);
    }

    const fetchCampaignRun = async () => {
        setLoading(true);
        try {
            let res = await campaignRunService.getById(campaignId);
            if (res.status < 400) {
                res = JSON.parse(res.body);
                if(isUpdate) {
                    if(res.status !== 'scheduled') {
                        navigate(-1);
                    }
                }
                setCampaignName(res.name);
                if(res.itemType === 'whatsapp') {
                    await renderWhatsappInitialData(res.phoneNumberId);
                    const campaignDetails = JSON.parse(res.details || '{}');
                    let retryCount = campaignDetails.retryCount;
                    if(!!retryCount) {
                        setAutoRetries(true);
                        setRetryCount(retryCount);
                    }
                    let currentTimeStamp = new Date().getTime();
                    let isScheduledCampaign = res.scheduleTimeStamp > currentTimeStamp;
                    setIsScheduled(isScheduledCampaign);
                    setScheduleTime(isScheduledCampaign ? res.scheduleTimeStamp : null);
                    handleTemplateSelect(campaignDetails?.templateId);
                    fetchSendToContactList(res);
                }
            }
        } catch (err) { errorHandler.handleError(err); }
        setLoading(false);
    };

    const renderWhatsappInitialData = (phoneNumberId)=> {
        setPhoneNumberId(phoneNumberId);
        fetchTemplateList();
        fetchTemplateFileList(currentLeadsId);
        fetchTemplateUseCount();
    }

    const fetchSendToContactList = async(campaign) => {
        try {
            let body = {
                campaignId: campaignId,
                page: 1,
                size: 15
            }
            const sendTo = JSON.parse(campaign.sendTo || '{}');
            let res = await campaignRunService.getCampaignRunContactList(body);
            if (res.status < 400) {
                const parsed = JSON.parse(res.body);
                const newList = parsed.result || [];
                newList.forEach(c =>
                    nameCardMapRef.current.set(c.id, c)
                );
                setSelectedItems(() => ({
                    labelCard: new Set(),
                    nameCard: new Set(sendTo)
                }))
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
    }

    const fetchTemplateUseCount = async() => {
        try {
            let response = await campaignRunViewService.getTemplateUseCountByProjectId(currentProjectId);
            if(response.status < 400) {
                response = JSON.parse(response.body);
                setTemplateUse(response);
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
    }

    const fetchTemplateList = async ()=> {
        try {
            let response = await kaiziqLeadsConnectionService.getTemplateListByLeadsIdWithLimit(currentLeadsId, 50);
            if(response.status < 400) {
                response = JSON.parse(response.body);
                let list = response.data.filter((item)=> item.status === 'APPROVED');
                setTemplateList(list);
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
    }

    const loadMoreLabels = async () => {
        if (loadingMore) return;
        try {
            setLoadingMore(true);
            const res = await registryLinkService.getFilteredLabelsForLeads({
                page: loadMorePage,
                size: 15,
                type: 'nameCard',
                isSubscribedList: [true],
                projectId: currentProjectId,
                searchQuery: debouncedSearch,
            });
            if (res.status < 400) {
                const parsed = JSON.parse(res.body);
                const newList = parsed.itemList || [];
                if (newList.length === 0) {
                    setHasMore(false);
                } else {
                    setSelectAllLabelList(prev => [...prev, ...newList]);
                    setLoadMorePage(prev => prev + 1);
                    setHasMore(newList.length === 10);
                }
            }
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoadingMore(false);
        }
    };

    const fetchLabels = async (type, page) => {
        try {
            setLoading(true);
            const apiPage = page - 1;
            const cacheKey = `${type}-${apiPage}`;

            if (dataCache.current[cacheKey]) {
                const cached = dataCache.current[cacheKey];
                setLabelsList(prev =>
                    prev === cached ? prev : cached
                );
                setLoading(false);
                return;
            }

            const res = await registryLinkService.getFilteredLabelsForLeads({
                page,
                size: 10,
                type,
                projectId: currentProjectId,
                searchQuery: debouncedSearch,
            });

            if (res.status < 400) {
                const parsed = JSON.parse(res.body);
                const list = parsed.itemList;
                if (type === 'labelCard') {
                    list.forEach(label =>
                        label.children?.forEach(c =>
                            nameCardMapRef.current.set(c.registryId, c)
                        )
                    );
                } else {
                    list.forEach(c =>
                        nameCardMapRef.current.set(c.id, c)
                    );
                }

                dataCache.current[cacheKey] = list;
                setLabelsList(list);
                setTotalPages(parsed.totalPages);
            }
        } catch (e) {
            errorHandler.handleError(e);
        } finally {
            setLoading(false);
        }
    };

    const refetchData = async ()=> {
        const res = await registryLinkService.getFilteredLabelsForLeads({
            page,
            size: 10,
            type: tabValue,
            projectId: currentProjectId,
            searchQuery: debouncedSearch,
        });

        if (res.status < 400) {
            const parsed = JSON.parse(res.body);
            const list = parsed.itemList;
            if (tabValue === 'labelCard') {
                list.forEach(label =>
                    label.children?.forEach(c =>
                        nameCardMapRef.current.set(c.registryId, c)
                    )
                );
            } else {
                list.forEach(c =>
                    nameCardMapRef.current.set(c.id, c)
                );
            }
            setLabelsList(list);
            setTotalPages(parsed.totalPages);
        }
    }

    const handleTemplateSelect = (value)=> {
        setVariableData({
            header: {},
            body: {}
        });
        renderTemplateData(value);
        setSelectedTemplateId(value);
    }

    const renderTemplateData = async (id)=> {
        try {
            let findTemplate = templateList.find((item)=> item.id === id);
            findTemplate = {...findTemplate, headerVariable: extractVariables(getHeader(findTemplate.components)),  variables: extractVariables(getBody(findTemplate.components))};
            let headerType = getContentType(findTemplate.components);
            setHeaderType(headerType);
            setTemplate(findTemplate);
            const initHeader = {};
            const initBody = {};
            findTemplate.headerVariable?.forEach(v => {
              initHeader[v] = {
                variableType: 'static',
                variableValue: ''
              };
            });
            findTemplate.variables?.forEach(v => {
              initBody[v] = {
                variableType: 'static',
                variableValue: ''
              };
            });
            setVariableData({
              header: initHeader,
              body: initBody
            });
        } catch(err) {
            errorHandler.handleError(err);
        }
    }

    const getBody = (components, example) => {
        const body = components.find(c => c.type === 'BODY');
        if (!body) return null;
        let text = body.text;
        if (body.example && body.example.body_text && body.example.body_text[0]) {
            body.example.body_text[0].forEach((val, idx) => {
                text = text.replace(new RegExp(`{{${idx+1}}}`, 'g'), val);
            });
        }
        return text;
    };

    const extractVariables = (content) => {
        const matches = content?.match(/{{(\w+)}}/g) || [];
        return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))];
    };

    const getHeader = (components) => {
        if(!!components) {
            const header = components.find(c => c.type === 'HEADER');
            if (!header) return null;
            if (header.format === 'TEXT') {
                return header.text;
            }
        }
        return '';
    };

    const getContentType = (components) => {
        if(!!components) {
            const header = components.find(c => c.type === 'HEADER');
            if (!header) return null;
            return header.format;
        }
        return '';
    }

    const handleSubmit = async (e) => {
        setCursor('wait');
        e.preventDefault();
        try {
            if (validateField()) {
                let templateObj = templateList.find((item)=> item.id === selectedTemplateId);
                let templateName = templateObj?.name;
                if(!templateName) return;
                let detailsObj = {
                    bodyText: bodyText,
                    templateDetails: variableData,
                    templateName: templateName,
                    autoRetries: autoRetries,
                    retryCount: parseInt(retryCount)
                };
                let sendToList = Array.from(selectedItems.nameCard);
                const uniqueRemoveIdNumbers = [...new Set(removedIdList)];
                let statsObj = {
                    total: sendToList.length,
                    openRate: 0,
                    clickRate: 0,
                    sent: 0
                }
                let status = isScheduled ? 'scheduled' : 'running';
                let time = isScheduled ? scheduleTime : new Date().getTime();
                let bodyObj = {
                    name: campaignName,
                    itemType: props?.itemType,
                    itemId: selectedTemplateId,
                    phoneNumberId: phoneNumberId,
                    leadsConnectionId: currentLeadsId,
                    projectId: currentProjectId,
                    details: JSON.stringify(detailsObj),
                    stats: JSON.stringify(statsObj),
                    sendTo : JSON.stringify(sendToList),
                    status: status,
                    scheduleTimeStamp: time,
                    selectAll: selectAllContact,
                    removedIdList: uniqueRemoveIdNumbers
                }
                let response;
                if(isUpdate) {
                    bodyObj.id = campaignId;
                    response = await campaignRunService.updateCampaignRun(bodyObj);
                } else {
                    if(isScheduled) {
                        response = await campaignRunService.createCampaignRun(bodyObj);
                    } else {
                        response = await campaignRunService.createCampaignRunNow(bodyObj);
                    }
                }
                if(response.status < 400) {
                    response = JSON.parse(response.body);
                    SnackbarManager.show(isUpdate ? 'Campaign Updated' : 'New campaign create', 'success')
                    handleBack();
                } else {
                    SnackbarManager.show(
                      `Error ${isUpdate ? 'updating' : 'creating'} campaign`,
                      'error'
                    );
                }
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const validateField = () => {
        const newErrors = {};
        if (!campaignName.trim()) {
            newErrors.campaignName = "Campaign Name is required";
        }
        if (!selectedTemplateId.trim()) {
            newErrors.templateId = "Template is required";
        }
        if (!phoneNumberId.trim()) {
            newErrors.phoneNumberId = "Phone Number is required";
        }
        if(isScheduled) {
            if (!scheduleTime) {
                newErrors.scheduleTime = "Schedule Time is required";
            }
        }
        if(!selectAllContact) {
            if (selectedItems.nameCard.size === 0) {
                newErrors.contacts = "Contact is required";
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const handleDate = (date) => {
        setScheduleTime(moment(date.$d).valueOf());
    }

    const toggleSchedule = ()=> {
        setIsScheduled(!isScheduled);
    }

    const toggleChangeFile = ()=> {
        setChangeFile(!changeFile);
    }

    const handleHeaderFileChange = (e)=> {
        e.preventDefault();
        let value = e.target.value;
        let findValue = currentFiles[value];
        const lookupResult = mime.lookup(findValue?.fileName);
        if (typeof lookupResult !== 'string' || !lookupResult) {
            SnackbarManager.show('File is not supported', 'error');
            return;
        };
        let fileType = lookupResult.split('/')[0];
        fileType = fileType?.toUpperCase();
        if(headerType === fileType) {
            handleHeaderUrl(headerType, findValue.publicUrl);
            setHeader({ format: headerType, str: findValue.publicUrl, fileId: value });
        } else {
            SnackbarManager.show('File is not supported', 'error');
            handleHeaderUrl(headerType, '');
            setHeader({ format: '', str: '', fileId: 0 });
        }
    }

    const handleFileChange = async (event) => {
        setCursor('wait');
        const file = event.target.files[0];
        if (!!file) {
            let templateObj = templateList.find((item)=> item.id === selectedTemplateId);
            let templateName = templateObj?.name;
            const result = await validate(file);
            if(result.isValid) {
                let filesObj = { ...files };
                const url = await convertFileToBase64(file);
                let newFileObj = {
                    file: url,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    projectId: currentProjectId
                }
                let bodyObj = {
                    leadsConnectionId: currentLeadsId,
                    projectId: currentProjectId,
                    templateName: templateName,
                    fileObj: {
                        tableType: 'LEADS_WHATSAPP',
                        itemId: currentLeadsId,
                        projectId: currentProjectId,
                        userId: activeUserId,
                        storeType: 'whatsappFile',
                        file: newFileObj,
                        fileName: newFileObj.fileName
                    },
                }
                let response = await addNewFileForTemplate(bodyObj);
                SnackbarManager.show('File Uploaded', 'success');
                if(response.status < 400) {
                    await fetchTemplateFileList(currentLeadsId);
                }
            } else {
                SnackbarManager.show(result.error, 'error');
            }
        }
        setCursor('auto');
    };

    const handleChoose = (event) => {
        inputRef.current.click();
    };

    const convertFileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const handleVariableData = (variableName, key, value)=> {
        setVariableData((prev)=> ({...prev, body: { ...prev.body, [variableName]: {...prev.body[variableName], [key]: value }}}));
    }

    const handleHeaderVariableData = (variableName, key, value) => {
        setVariableData((prev)=> ({...prev, header: {...prev.header, [variableName]: {...prev.header[variableName], [key]: value }}}));
    }

    const handleHeaderUrl = (variableName, value) => {
        setVariableData((prev)=> ({...prev, header: {...prev.header, [variableName]: {...prev.header[variableName], link: value}}}));
    }

    const handlePhoneNumberValue = (value)=> {
        setPhoneNumberId(value);
    }

    const handleBack = ()=> {
        navigate(-1);
    }

    const handleRemoveSelectAll = (id)=> {
        setRemovedIdList([...removedIdList, id]);
    }

    const handlePrevious = ()=> {
        if(step == 0) {
            setStep(1);
        } else {
            setStep((prev)=> prev - 1);
        }
    }

    const handleNext = ()=> {
        if(verifySteps()) {
            setStep((prev)=> prev + 1);
        }
    }

    const verifySteps =()=> {
        const newErrors = {};
        if(step === 0) {
            if (!campaignName.trim()) {
                newErrors.campaignName = "Campaign Name is required";
            }
            if (!phoneNumberId.trim()) {
                newErrors.phoneNumberId = "Phone Number is required";
            }
            if (isScheduled && !scheduleTime) {
                newErrors.scheduleTime = "Schedule Time is required";
            }
        }
        if(step === 1) {
            if(!selectAllContact) {
                if (selectedItems.nameCard.size === 0) {
                    newErrors.contacts = "Contact is required";
                }
            }
        }
        if(step === 2) {
            if (!selectedTemplateId.trim()) {
                newErrors.templateId = "Template is required";
            }
            let checkVariableData = getVariableDataValue();
            if(!checkVariableData) {
                newErrors.variableData = 'Please fill default values';
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const getVariableDataValue = ()=> {
        let bodyKeys = Object.keys(variableData?.body || {});
        if(bodyKeys.length > 0) {
            for(let key of bodyKeys) {
                let variableValue = variableData.body[key].variableValue;
                if(!variableValue) return false;
                if(variableValue.length <= 0) return false;
            }
        }
        let headerKeys = Object.keys(variableData?.header || {});
        if(headerKeys.length > 0) {
            for(let key of headerKeys) {
                let variableValue = variableData.header[key].variableValue;
                if(!variableValue) return false;
                if(variableValue.length <= 0) return false;
            }
        }
        return true;
    }

    const labelMap = useMemo(() => {
        const map = new Map();
        labelsList.forEach(l => map.set(l.id, l));
        return map;
    }, [labelsList]);

    const nameCardList = useMemo(() => {
        return Array.from(selectedItems.nameCard)
            .map(id => nameCardMapRef.current.get(id))
            .filter(Boolean);
    }, [selectedItems.nameCard]);

    const handleItemToggle = (id) => {
        setSelectedItems(prev => {
            const currentSet = prev[tabValue];
            const updatedTabSet = new Set(currentSet);
            const isSelecting = !updatedTabSet.has(id);

            isSelecting ? updatedTabSet.add(id) : updatedTabSet.delete(id);

            if (
                updatedTabSet.size === currentSet.size &&
                [...updatedTabSet].every(v => currentSet.has(v))
            ) {
                return prev;
            }

            if (tabValue !== 'labelCard') {
                return { ...prev, [tabValue]: updatedTabSet };
            }

            const label = labelMap.get(id);
            const updatedNameCard = new Set(prev.nameCard);

            label?.children?.forEach(child => {
                if (isSelecting) {
                    updatedNameCard.add(child.registryId);
                } else {
                    const stillCoveredByAnotherLabel = [...updatedTabSet].some(selectedLabelId => {
                        const otherLabel = labelMap.get(selectedLabelId);
                        return otherLabel?.children?.some(c => c.registryId === child.registryId);
                    });

                    if (!stillCoveredByAnotherLabel) {
                        updatedNameCard.delete(child.registryId);
                    }
                }
            });

            return {
                labelCard: updatedTabSet,
                nameCard: updatedNameCard
            };
        });
    };

    const handleSelectAll = () => {
        setSelectedItems(prev => {
            const updatedTabSet = new Set(prev[tabValue]);

            const allSelected = labelsList.every(i => updatedTabSet.has(i.id));

            if (tabValue === 'nameCard') {
                labelsList.forEach(item => {
                    allSelected ? updatedTabSet.delete(item.id) : updatedTabSet.add(item.id);
                });
                return { ...prev, nameCard: updatedTabSet };
            }

            const updatedNameCard = new Set(prev.nameCard);

            labelsList.forEach(label => {
                allSelected ? updatedTabSet.delete(label.id) : updatedTabSet.add(label.id);

                label.children?.forEach(child => {
                    if (allSelected) {
                        const stillCovered = [...updatedTabSet].some(selectedLabelId => {
                            const otherLabel = labelMap.get(selectedLabelId);
                            return otherLabel?.children?.some(c => c.registryId === child.registryId);
                        });
                        if (!stillCovered) {
                            updatedNameCard.delete(child.registryId);
                        }
                    } else {
                        updatedNameCard.add(child.registryId);
                    }
                });
            });

            return {
                labelCard: updatedTabSet,
                nameCard: updatedNameCard
            };
        });
    };

    const isAllSelected = labelsList.length > 0 && labelsList.every(i => selectedItems[tabValue].has(i.id));

    const toggleSelectAll = (e) => {
        e?.stopPropagation();
        handleSelectAllContact();
        if (!selectAllContact) {
            setCurrentView('selectAll');
        }
    }

    const handleSelectAllContact = () => {
        setSelectAllContact((prev)=> !prev);
    }

    const headerSection = {
        0 : {
            title: 'Campaign Settings',
            subTitle: 'Configure campaign details and delivery settings'
        },
        1 : {
            title: 'Select Audience',
            subTitle: 'Choose who will receive this campaign'
        },
        2: {
            title: 'Template Configuration',
            subTitle: 'Select a WhatsApp template and configure variables'
        },
        3: {
            title: 'Campaign Preview',
            subTitle: 'Review all campaign settings before sending'
        }
    }

    const handleRetryCount = (count)=> {
        setRetryCount(count)
    }

    const RunCampaignSkeleton = () => {
        return (
            <Box
                sx={{
                    width: 420,
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: "#fff"
                }}>
                <Stack spacing={2.5}>
                    <Skeleton variant="text" width="60%" height={32} />
                    <Stack spacing={1}>
                        <Skeleton variant="text" width={120} />
                        <Skeleton variant="rounded" height={44} />
                    </Stack>
                    <Stack spacing={1}>
                        <Skeleton variant="text" width={120} />
                        <Skeleton variant="rounded" height={44} />
                    </Stack>
                    <Stack spacing={1}>
                        <Skeleton variant="text" width={160} />
                        <Skeleton variant="rounded" height={44} />
                    </Stack>
                    <Stack spacing={1}>
                        <Skeleton variant="text" width={90} />
                        <Skeleton variant="rounded" height={44} />
                    </Stack>
                    <Skeleton variant="rounded" height={44} />
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Skeleton variant="rounded" width={90} height={36} />
                        <Skeleton variant="rounded" width={130} height={36} />
                    </Stack>
                </Stack>
            </Box>
        );
    };

    const contactSelectionPanel = () => {
        return (
            <div className="ccm-sheet">
                {loading ? (
                    <div className="ccm-loading">
                        <Stack spacing={1.5}>
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Skeleton key={i} variant="rounded" height={48} sx={{ borderRadius: "10px", bgcolor: "rgba(99,102,241,0.07)" }} />
                            ))}
                        </Stack>
                    </div>
                ) : (
                    <div className="ccm-body">
                        <div className="ccm-body-header flex-row-center">
                            <Tabs
                                value={tabValue}
                                onChange={(_, v) => { setTabValue(v); setPage(1); setCurrentView('registries'); setSelectAllContact(false); }}
                                className="ccm-tabs"
                                TabIndicatorProps={{ className: "ccm-tab-indicator" }}>
                                <Tab label="Labels" value="labelCard" className="ccm-tab" />
                                <Tab label="Contacts" value="nameCard" className="ccm-tab" />
                                <div className='flex-row-center'>
                                    <Checkbox
                                        checked={selectAllContact}
                                        onChange={toggleSelectAll}
                                        size="small"
                                        sx={{
                                            color: "#6366f1",
                                            "&.Mui-checked": { color: "#6366f1" },
                                            "&.MuiCheckbox-indeterminate": { color: "#a78bfa" },
                                        }}
                                    />
                                    <span className="ccm-panel-head-text">Select All</span>
                                </div>
                            </Tabs>
                            {currentView !== 'selectAll' &&
                                <div className="ccm-search-wrap">
                                    <Search
                                        size={16}
                                        className="failed__msg__view__search-icon"
                                    />
                                    <input
                                        className="failed__msg__view__search-input"
                                        placeholder="Search name or number"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            }
                        </div>
                        <div className="ccm-panels">
                            {
                                currentView === 'selectAll' ?
                                    <>
                                        <div className="ccm-panel ccm-panel-right">
                                            <div className="ccm-panel-head ccm-panel-head--right">
                                                <span className="ccm-panel-head-text">Selected</span>
                                                <span className="ccm-badge ccm-badge--accent">All Contacts</span>
                                            </div>
                                            <div className="ccm-scroll" ref={scrollRef}>
                                                {selectAllLabelList.length === 0 ? (
                                                    <div className="ccm-empty">
                                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                            <circle cx="9" cy="7" r="4" />
                                                            <line x1="20" y1="8" x2="20" y2="14" />
                                                            <line x1="17" y1="11" x2="23" y2="11" />
                                                        </svg>
                                                        <p>No contacts yet</p>
                                                        <span>Select from the left panel</span>
                                                    </div>
                                                ) : (
                                                    selectAllLabelList.map((c, i) => (
                                                        <div key={c.id} className="ccm-item ccm-item--selected ccm-item--right" style={{ animationDelay: `${i * 25}ms` }}>
                                                            <div className="ccm-avatar">{c.name?.[0]?.toUpperCase()}</div>
                                                            <div className="ccm-item-content">
                                                                <span className="ccm-item-name">{c.name}</span>
                                                                {c?.contactNumber && <span className="ccm-item-phone">{c.contactNumber}</span>}
                                                            </div>
                                                            <button
                                                                className="ccm-remove-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveSelectAll(c.id);
                                                                    setSelectAllLabelList((prev)=> prev.filter((item)=> item.id !== c.id));
                                                                }}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ))
                                                )}
                                               {hasMore && (
                                                   <div
                                                       ref={ref}
                                                       className={className + '__loadMore'}>
                                                       {loadingMore && (
                                                           <>
                                                               <CircularProgress size={24} />
                                                               <span>Loading more...</span>
                                                           </>
                                                       )}
                                                   </div>
                                               )}
                                            </div>

                                        </div>
                                    </>
                                :
                                <>
                                    <div className="ccm-panel ccm-panel-left">
                                        <div className="ccm-panel-head">
                                            <Checkbox
                                                checked={isAllSelected}
                                                indeterminate={selectedItems[tabValue].size > 0 && !isAllSelected}
                                                onChange={handleSelectAll}
                                                size="small"
                                                sx={{
                                                    color: "#6366f1",
                                                    "&.Mui-checked": { color: "#6366f1" },
                                                    "&.MuiCheckbox-indeterminate": { color: "#a78bfa" },
                                                }}
                                            />
                                            <span className="ccm-panel-head-text">Available</span>
                                            <span className="ccm-badge">{labelsList.length}</span>
                                        </div>

                                        <div className="ccm-scroll">
                                            {labelsList.map((item, i) => {
                                                const isSelected = selectedItems[tabValue].has(item.id);
                                                return (
                                                    <div
                                                        key={item.id}
                                                        className={`ccm-item ${isSelected ? "ccm-item--selected" : ""}`}
                                                        onClick={() => handleItemToggle(item.id)}
                                                        style={{ animationDelay: `${i * 30}ms` }}
                                                    >
                                                        <Checkbox
                                                            checked={isSelected}
                                                            size="small"
                                                            onClick={e => e.stopPropagation()}
                                                            onChange={() => handleItemToggle(item.id)}
                                                            sx={{
                                                                color: "#6366f1",
                                                                "&.Mui-checked": { color: "#6366f1" },
                                                            }}
                                                        />
                                                        <div className="ccm-item-content">
                                                            <span className="ccm-item-name">{item.name}</span>
                                                            {item?.contactNumber && <span className="ccm-item-phone">{item.contactNumber}</span>}
                                                        </div>
                                                        {isSelected && (
                                                            <span className="ccm-item-check">
                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                                    <polyline points="20 6 9 17 4 12" />
                                                                </svg>
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="ccm-pagination">
                                            <Pagination
                                                count={totalPages}
                                                page={page}
                                                onChange={(_, v) => setPage(v)}
                                                size="small"
                                                sx={{
                                                    "& .MuiPaginationItem-root": {
                                                        color: "#94a3b8",
                                                        fontFamily: "inherit",
                                                        fontSize: "12px",
                                                        "&.Mui-selected": {
                                                            backgroundColor: "#6366f1",
                                                            color: "#fff",
                                                        },
                                                        "&:hover": {
                                                            backgroundColor: "rgba(99,102,241,0.1)",
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="ccm-divider">
                                        <div className="ccm-divider-line" />
                                        <div className="ccm-divider-arrow">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M5 12h14M12 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                        <div className="ccm-divider-line" />
                                    </div>
                                    <div className="ccm-panel ccm-panel-right">
                                        <div className="ccm-panel-head ccm-panel-head--right">
                                            {
                                                selectedItems.nameCard.size > 0 &&
                                                    <Checkbox
                                                        checked={false}
                                                        indeterminate={selectedItems.nameCard.size > 0}
                                                        onChange={()=>{
                                                            setSelectedItems(()=>({
                                                                labelCard: new Set(),
                                                                nameCard: new Set()
                                                            }));
                                                        }}
                                                        size="small"
                                                        sx={{
                                                            color: "#6366f1",
                                                            "&.Mui-checked": { color: "#6366f1" },
                                                            "&.MuiCheckbox-indeterminate": { color: "#a78bfa" },
                                                        }}
                                                    />
                                            }
                                            <span className="ccm-panel-head-text">Selected</span>
                                            <span className="ccm-badge ccm-badge--accent">{nameCardList.length}</span>
                                        </div>

                                        <div className="ccm-scroll">
                                            {nameCardList.length === 0 ? (
                                                <div className="ccm-empty">
                                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                        <circle cx="9" cy="7" r="4" />
                                                        <line x1="20" y1="8" x2="20" y2="14" />
                                                        <line x1="17" y1="11" x2="23" y2="11" />
                                                    </svg>
                                                    <p>No contacts yet</p>
                                                    <span>Select from the left panel</span>
                                                </div>
                                            ) : (
                                                nameCardList.map((c, i) => (
                                                    <div key={c.id} className="ccm-item ccm-item--selected ccm-item--right" style={{ animationDelay: `${i * 25}ms` }}>
                                                        <div className="ccm-avatar">{c.name?.[0]?.toUpperCase()}</div>
                                                        <div className="ccm-item-content">
                                                            <span className="ccm-item-name">{c.name}</span>
                                                            {c?.contactNumber && <span className="ccm-item-phone">{c.contactNumber}</span>}
                                                        </div>
                                                        <button
                                                            className="ccm-remove-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();

                                                                setSelectedItems(prev => {
                                                                    const updatedNameCard = new Set(prev.nameCard);
                                                                    updatedNameCard.delete(c.registryId || c.id);

                                                                    return {
                                                                        ...prev,
                                                                        nameCard: updatedNameCard
                                                                    };
                                                                });
                                                            }}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className='campaignForm' style={{cursor: loading ? 'wait' : 'auto'}}>
            <div className="cf-header">
                <div className="cf-header-left">
                    <div className="cf-header-icon">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                        </svg>
                    </div>
                    <div>
                        {(() => {
                            let header = headerSection[step];
                            return (
                                <>
                                    <p className="cf-eyebrow">{header.title ?? 'Campaigns'}</p>
                                    <p className="cf-subtitle">{header.subTitle}</p>
                                </>
                            )
                        })()}
                    </div>
                </div>
                <div className="cf-stepper">
                    {[
                        'Campaign',
                        'Audience',
                        'Template',
                        'Review'
                    ].map((label, index) => (
                        <div
                            key={label}
                            className={`cf-step ${step >= index ? 'active' : ''}`}
                        >
                            <div className="cf-step-circle">
                                {step > index ? '✓' : index + 1}
                            </div>
                            <span>{label}</span>
                        </div>
                    ))}
                </div>
                <button className="cf-close" onClick={handleBack} aria-label="Close">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>
            </div>
            {
                step === 0 &&
                    <div className='cf-form-scroll' style={{backgroundColor: '#ffffff'}}>
                        <div className="cf-field">
                            <div className='cf-field-header'>
                                <label className="cf-label">Campaign Name <span className="cf-req">*</span></label>
                                <div className='flex-row-center-1vw-gap'>
                                    <div className='cf-checkbox-group' onClick={()=> setAutoRetries(prev=> !prev)}>
                                        <Checkbox
                                            checked={autoRetries}
                                            size="small"
                                            sx={{
                                                color: "#6366f1",
                                                "&.Mui-checked": { color: "#6366f1" },
                                                "&.MuiCheckbox-indeterminate": { color: "#a78bfa" },
                                            }}
                                        />
                                        <span className="cf-checkbox-label">Auto Retries</span>
                                        <Tooltip
                                            title='Messages that fail due to temporary Healthy Ecosystem errors (rate limits) will be retried automatically to improve delivery success. You can enable or disable auto retries for this campaign anytime.'
                                            placement="top-start">
                                            <Info size={14} color='#6366f1' style={{marginLeft: '3px'}} />
                                        </Tooltip>
                                    </div>
                                    {
                                        autoRetries &&
                                            <div className='flex-row-center-1vw-gap'>
                                                <label className="cf-label">Retry Count <span className="cf-req">*</span></label>
                                                <div className="cf-select-wrap">
                                                    <select className="cf-select" value={retryCount} disabled={!autoRetries ? true : false} onChange={(e) => handleRetryCount(e.target.value)}>
                                                        <option value={1}>1 Times</option>
                                                        <option value={3}>3 Times</option>
                                                        <option value={6}>6 Times</option>
                                                    </select>
                                                    <svg className="cf-select-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                                                </div>
                                                <Tooltip
                                                    title={
                                                      'If the initial WhatsApp message fails due to a Healthy Ecosystem error, we retry sending it ' +
                                                      retryCount +
                                                      ' additional time' +
                                                      (retryCount === 1 ? '' : 's')
                                                    }
                                                    placement="top-start">
                                                    <Info size={14} color='#6366f1' style={{marginLeft: '3px'}} />
                                                </Tooltip>
                                            </div>
                                    }
                                </div>
                            </div>
                            <Input
                                type="text"
                                placeholder="e.g. WhatsApp Product Update"
                                value={campaignName}
                                style={{marginBottom: '0'}}
                                onChange={(e) => setCampaignName(e.target.value)}
                                error={errors?.campaignName}
                            />
                        </div>
                        <div className="cf-field">
                            <label className="cf-label">WhatsApp Number <span className="cf-req">*</span></label>
                            <div className="cf-select-wrap">
                                <select className="cf-select" value={phoneNumberId} disabled={true} onChange={(e) => handlePhoneNumberValue(e.target.value)}>
                                    <option key={currentPhoneNumber?.id} value={currentPhoneNumber?.id}>{currentPhoneNumber?.display_phone_number}</option>
                                </select>
                                <svg className="cf-select-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                            </div>
                            {errors?.phoneNumberId && <p className="cf-err">{errors?.phoneNumberId}</p>}
                        </div>
                        <div className="cf-field">
                            <div className='cf-checkbox-group' onClick={toggleSchedule}>
                                <Checkbox
                                    checked={isScheduled}
                                    size="small"
                                    sx={{
                                        color: "#6366f1",
                                        "&.Mui-checked": { color: "#6366f1" },
                                        "&.MuiCheckbox-indeterminate": { color: "#a78bfa" },
                                    }}
                                />
                                <span className="cf-checkbox-label">Schedule Campaign</span>
                                    <Tooltip
                                        title='Schedule your campaign to be sent at a specific date and time. This allows you to plan campaigns in advance and ensure delivery at optimal times.'
                                        placement="top-start">
                                    <Info size={14} color='#6366f1' style={{marginLeft: '3px'}} />
                                </Tooltip>
                            </div>
                        </div>

                        {
                            isScheduled  &&
                                <div className="cf-field">
                                    <label className="cf-label">Schedule <span className="cf-req">*</span></label>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DateTimePicker
                                            value={scheduleTime ? dayjs(scheduleTime) : null}
                                            onChange={handleDate}
                                            minutesStep={15}
                                            disablePast
                                            slotProps={{ textField: { fullWidth: true, size: 'small', className: 'cf-datetime' } }}
                                        />
                                    </LocalizationProvider>
                                    {errors?.scheduleTime && <p className="cf-err">{errors?.scheduleTime}</p>}
                                </div>
                        }
                    </div>

            }
            {
                step === 1 && contactSelectionPanel()
            }
            {
                step === 2 &&
                    <div className="cf-right">
                        <div className="cf-vars-col">
                            <div className="cf-field template-type">
                                <label className="cf-label">Template Type <span className="cf-req">*</span></label>
                                <div className="cf-select-wrap">
                                    <select className="cf-select" value={selectedTemplateId} onChange={(e) => handleTemplateSelect(e.target.value)}>
                                        <option value="" disabled>Select template</option>
                                        {!!templateList && templateList.length !== 0 &&
                                            templateList?.map((template) => (
                                                <option key={template.id} value={template.id}>{template?.name.toUpperCase()}</option>
                                            ))
                                        }
                                    </select>
                                    <svg className="cf-select-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                                </div>
                                {errors?.templateId && <p className="cf-err">{errors?.templateId}</p>}
                            </div>
                            {
                                !!templateUse[selectedTemplateId] &&
                                    <>
                                        <div className='wa-warning'>
                                            ⚠️ <strong>Suggestion:</strong> We recommend using different templates within the same week for the same set of users to avoid being flagged by WhatsApp as spam. This helps maintain better message delivery and keeps your communication more effective and compliant with WhatsApp guidelines.
                                        </div>
                                    </>
                            }
                            {props.itemType === 'whatsapp' ? (
                                !!template && (
                                    <div className="cf-vars-scroll">
                                        {template?.headerVariable?.length > 0 && (
                                            <div className="cf-vars-block">
                                                <p className="cf-vars-title">
                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                                                    Header Variables
                                                </p>
                                                {template?.headerVariable?.map((v, i) => (
                                                    <div key={i} className="cf-var-card">
                                                        <div className="cf-var-row">
                                                            <span className="cf-var-tag">{v}</span>
                                                            <div className="cf-select-wrap cf-select-wrap--xs">
                                                                <select className="cf-select cf-select--xs" value={variableData?.header[v]?.variableType ?? 'static'} onChange={(e) => handleHeaderVariableData(v, 'variableType', e.target.value)}>
                                                                    <option value="static">Static</option>
                                                                    <option value="dynamic">Dynamic</option>
                                                                </select>
                                                                <svg className="cf-select-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                                                            </div>
                                                        </div>
                                                        {variableData?.header[v]?.variableType === 'dynamic' ? (
                                                            <div className="cf-var-row">
                                                                <div className="cf-select-wrap cf-select-wrap--xs">
                                                                    <select className="cf-select cf-select--xs" value={variableData?.header[v]?.variableValue ?? ''} onChange={(e) => handleHeaderVariableData(v, 'variableValue', e.target.value)}>
                                                                        <option value="">Pick field</option>
                                                                        <option value="name">Name</option>
                                                                        <option value="email">Email</option>
                                                                        <option value="contact">Contact</option>
                                                                        <option value="designation">Designation</option>
                                                                        <option value="address">Address</option>
                                                                        <option value="state">State</option>
                                                                    </select>
                                                                    <svg className="cf-select-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                                                                </div>
                                                                {!!variableData?.header[v]?.variableValue && (
                                                                    <Input placeholder="Default value" value={variableData?.header[v]?.defaultValue ?? ''} onChange={(e) => handleHeaderVariableData(v, 'defaultValue', e.target.value)} />
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <Input placeholder="Type value" value={variableData?.header[v]?.variableValue ?? ''} onChange={(e) => handleHeaderVariableData(v, 'variableValue', e.target.value)} />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {template?.variables?.length > 0 && (
                                            <div className="cf-vars-block">
                                                <p className="cf-vars-title">
                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                                    Body Variables
                                                </p>
                                                {template?.variables?.map((v, i) => (
                                                    <div key={i} className="cf-var-card">
                                                        <div className="cf-var-row">
                                                            <span className="cf-var-tag">{v}</span>
                                                            <div className="cf-select-wrap cf-select-wrap--xs">
                                                                <select className="cf-select cf-select--xs" value={variableData?.body[v]?.variableType ?? 'static'} onChange={(e) => handleVariableData(v, 'variableType', e.target.value)}>
                                                                    <option value="static">Static</option>
                                                                    <option value="dynamic">Dynamic</option>
                                                                </select>
                                                                <svg className="cf-select-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                                                            </div>
                                                        </div>
                                                        {variableData?.body[v]?.variableType === 'dynamic' ? (
                                                            <div className="cf-var-row">
                                                                <div className="cf-select-wrap cf-select-wrap--xs">
                                                                    <select className="cf-select cf-select--xs" value={variableData?.body[v]?.variableValue ?? ''} onChange={(e) => handleVariableData(v, 'variableValue', e.target.value)}>
                                                                        <option value="">Pick field</option>
                                                                        <option value="name">Name</option>
                                                                        <option value="email">Email</option>
                                                                        <option value="contact">Contact</option>
                                                                        <option value="designation">Designation</option>
                                                                        <option value="address">Address</option>
                                                                        <option value="state">State</option>
                                                                    </select>
                                                                    <svg className="cf-select-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                                                                </div>
                                                                {!!variableData?.body[v]?.variableValue && (
                                                                    <Input placeholder="Default value" value={variableData?.body[v]?.defaultValue ?? ''} onChange={(e) => handleVariableData(v, 'defaultValue', e.target.value)} />
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <Input placeholder="Type value" value={variableData?.body[v]?.variableValue ?? ''} onChange={(e) => handleVariableData(v, 'variableValue', e.target.value)} />
                                                                {errors?.variableData && <p className="cf-err">{errors?.variableData}</p>}
                                                            </>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {(headerType === 'VIDEO' || headerType === 'IMAGE') && (
                                            <div className="cf-vars-block">
                                                <div className='cf-vars-block-header flex-row-center-space-between'>
                                                    <p className="cf-vars-title">
                                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                                                        Header {headerType}
                                                    </p>
                                                    <input
                                                        ref={inputRef}
                                                        type='file'
                                                        accept="image/jpeg, image/jpe, image/png, video/mp4, video/3gpp"
                                                        onChange={handleFileChange}
                                                        style={{ display: 'none' }}
                                                    />
                                                    <button type="button" className="cf-btn cf-btn-primary" disabled={cursor === 'wait'} onClick={handleChoose}>
                                                        <FilePlus size={16}/>
                                                        Change File
                                                    </button>
                                                </div>
                                                {headerType === 'IMAGE' && !!variableData?.header[headerType]?.link && (
                                                    <div className="cf-media-preview"><img src={variableData?.header[headerType]?.link} alt="Header" /></div>
                                                )}
                                                {headerType === 'VIDEO' && !!variableData?.header[headerType]?.link && (
                                                    <div className="cf-media-preview">
                                                        <video controls style={{ width: '100%', borderRadius: 6 }}>
                                                            <source src={variableData?.header[headerType]?.link} type="video/mp4" />
                                                        </video>
                                                    </div>
                                                )}
                                                {errors?.headerVariable && <p className="cf-err">{errors?.headerVariable}</p>}
                                            </div>
                                        )}
                                    </div>
                                )
                            ) : (
                                <div className="cf-field" style={{ flex: 1, padding: '16px' }}>
                                    <label className="cf-label">Content Body <span className="cf-req">*</span></label>
                                    <TextArea />
                                </div>
                            )}
                        </div>

                        <div className="cf-preview-col">
                            <div className="cf-preview-topbar">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                                </svg>
                                Preview
                            </div>
                            <div className="cf-preview-body">
                                {!!selectedTemplateId ? (
                                    <WhatsappTemplatePreview id={selectedTemplateId} hideBackground={true} showReadMore={false} whatsappFile={whatsappFile} />
                                ) : (
                                    <div className="cf-preview-empty">
                                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="5" y="2" width="14" height="20" rx="2"/><line x1="9" y1="7" x2="15" y2="7"/><line x1="9" y1="11" x2="15" y2="11"/><line x1="9" y1="15" x2="12" y2="15"/>
                                        </svg>
                                        <span>Select a template to preview</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
            }
            {
                step === 3 &&
                    <div className="preview">
                        <div className="preview__left">
                            <section className="preview__card preview__overviewCard">
                                <div className="preview__cardHeader">
                                    <div className="preview__cardHeaderIcon">
                                        <svg
                                            width="17"
                                            height="17"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M3 7h18" />
                                            <path d="M6 3h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
                                            <path d="M8 11h8" />
                                            <path d="M8 15h5" />
                                        </svg>
                                    </div>

                                    <div>
                                        <div className="preview__heading">
                                            Campaign Overview
                                        </div>

                                        <div className="preview__description">
                                            Review your campaign configuration
                                        </div>
                                    </div>
                                </div>

                                <div className="preview__metaGrid">

                                    <div className="preview__meta">
                                        <div className="preview__metaIcon preview__metaIcon--purple">
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <path d="M4 4h16v16H4z" />
                                                <path d="M8 8h8" />
                                                <path d="M8 12h6" />
                                            </svg>
                                        </div>

                                        <div className="preview__metaContent">
                                            <span>Campaign name</span>
                                            <strong title={campaignName || "-"}>
                                                {campaignName || "-"}
                                            </strong>
                                        </div>
                                    </div>

                                    <div className="preview__meta">
                                        <div className="preview__metaIcon preview__metaIcon--green">
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.08 5.18 2 2 0 0 1 5.06 3h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.62 2.63a2 2 0 0 1-.45 2.11L9 10.73a16 16 0 0 0 4.27 4.27l1.27-1.27a2 2 0 0 1 2.11-.45c.85.29 1.73.5 2.63.62A2 2 0 0 1 21 15.9z" />
                                            </svg>
                                        </div>

                                        <div className="preview__metaContent">
                                            <span>WhatsApp number</span>
                                            <strong>
                                                {currentPhoneNumber
                                                    ?.display_phone_number
                                                    ?.toUpperCase() || "-"}
                                            </strong>
                                        </div>
                                    </div>

                                    <div className="preview__meta">
                                        <div className="preview__metaIcon preview__metaIcon--blue">
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <circle cx="12" cy="12" r="9" />
                                                <path d="M12 7v5l3 2" />
                                            </svg>
                                        </div>

                                        <div className="preview__metaContent">
                                            <span>
                                                Campaign {isScheduled ? "schedule" : "run"}
                                            </span>

                                            <strong>
                                                {scheduleTime
                                                    ? moment(scheduleTime).format(
                                                          "MMM DD, YYYY · HH:mm"
                                                      )
                                                    : "Run immediately"}
                                            </strong>
                                        </div>
                                    </div>

                                    <div className="preview__meta">
                                        <div className="preview__metaIcon preview__metaIcon--orange">
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
                                            </svg>
                                        </div>

                                        <div className="preview__metaContent">
                                            <span>Message template</span>
                                            <strong title={template?.name || "-"}>
                                                {template?.name || "-"}
                                            </strong>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <section className="preview__card preview__audienceCard">

                                <div className="preview__cardHeader">
                                    <div className="preview__cardHeaderIcon preview__cardHeaderIcon--green">
                                        <svg
                                            width="17"
                                            height="17"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                            <circle cx="9" cy="7" r="4" />
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                        </svg>
                                    </div>

                                    <div className="preview__audienceHeaderText">
                                        <div className="preview__heading">
                                            Audience
                                        </div>

                                        <div className="preview__description">
                                            Contacts who will receive this campaign
                                        </div>
                                    </div>

                                    <div
                                        className={`preview__audienceStatus ${
                                            selectAllContact
                                                ? "preview__audienceStatus--all"
                                                : ""
                                        }`}
                                    >
                                        <span className="preview__statusDot" />
                                        {selectAllContact
                                            ? "Entire audience"
                                            : "Custom selection"}
                                    </div>
                                </div>

                                <div className="preview__audienceBody">

                                    <div className="preview__recipientCount">
                                        <div className="preview__bigNumber">
                                            {selectAllContact
                                                ? "All"
                                                : selectedItems.nameCard.size.toLocaleString()}
                                        </div>

                                        <div className="preview__subText">
                                            {selectAllContact
                                                ? "contacts"
                                                : "recipients"}
                                        </div>
                                    </div>

                                    {!selectAllContact && (
                                        <div className="preview__avatarsWrap">
                                            <div className="preview__avatars">
                                                {nameCardList.slice(0, 5).map(
                                                    (contact, index) => (
                                                        <div
                                                            key={
                                                                contact.id ||
                                                                contact.registryId
                                                            }
                                                            className="preview__avatar"
                                                            style={{
                                                                zIndex:
                                                                    10 - index
                                                            }}
                                                            title={
                                                                contact.name
                                                            }
                                                        >
                                                            {contact.name
                                                                ?.charAt(0)
                                                                ?.toUpperCase() ||
                                                                "?"}
                                                        </div>
                                                    )
                                                )}

                                                {nameCardList.length > 5 && (
                                                    <div className="preview__avatar preview__avatar--more">
                                                        +{nameCardList.length - 5}
                                                    </div>
                                                )}
                                            </div>

                                            <span className="preview__avatarLabel">
                                                Selected contacts
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="preview__audienceFooter">
                                    <div className="preview__audienceInfo">
                                        <div className="preview__infoIcon">
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <circle cx="12" cy="12" r="9" />
                                                <path d="M12 11v5" />
                                                <path d="M12 8h.01" />
                                            </svg>
                                        </div>

                                        <span>
                                            {selectAllContact
                                                ? "Your campaign will be sent to all available contacts."
                                                : `${nameCardList.length.toLocaleString()} selected contact${
                                                      nameCardList.length !== 1
                                                          ? "s"
                                                          : ""
                                                  } will receive this message.`}
                                        </span>
                                    </div>
                                </div>
                            </section>

                        </div>
                        <div className="preview__right">

                            <section className="preview__templateCard">

                                {/* Header */}
                                <div className="preview__previewHeader">
                                    <div className="preview__previewHeaderLeft">
                                        <div className="preview__whatsappIcon">
                                            <svg
                                                width="17"
                                                height="17"
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                            >
                                                <path d="M20.52 3.48A11.86 11.86 0 0 0 12.05 0C5.5 0 .17 5.32.17 11.88c0 2.09.55 4.13 1.6 5.94L.06 24l6.32-1.66a11.9 11.9 0 0 0 5.67 1.44h.01c6.55 0 11.88-5.33 11.88-11.88 0-3.17-1.23-6.15-3.42-8.42ZM12.06 21.77h-.01a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.75.98 1-3.66-.23-.38a9.85 9.85 0 0 1-1.51-5.24C2.17 6.44 6.61 2 12.05 2c2.64 0 5.12 1.03 6.98 2.89a9.84 9.84 0 0 1 2.9 6.99c0 5.44-4.43 9.89-9.87 9.89Z" />
                                                <path d="M17.53 14.55c-.3-.15-1.77-.87-2.05-.97-.28-.1-.48-.15-.68.15-.2.3-.78.97-.96 1.17-.18.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.64-2.06-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.68-1.64-.93-2.25-.24-.59-.49-.51-.68-.52h-.58c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.87 1.22 3.07c.15.2 2.1 3.2 5.08 4.49.71.31 1.27.49 1.7.63.72.23 1.38.2 1.9.12.58-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35Z" />
                                            </svg>
                                        </div>

                                        <div>
                                            <div className="preview__previewTitle">
                                                Template Preview
                                            </div>

                                            <div className="preview__previewSubtitle">
                                                WhatsApp message
                                            </div>
                                        </div>
                                    </div>

                                    <div className="preview__liveBadge">
                                        <span />
                                        Live preview
                                    </div>
                                </div>

                                {/* Preview */}
                                <div className="preview__previewBox">
                                    {selectedTemplateId ? (
                                        <div className="preview__message">
                                            <WhatsappTemplatePreview
                                                hideBackground
                                                showReadMore={false}
                                                id={selectedTemplateId}
                                            />

                                            <div className="preview__messageTime">
                                                10:24 AM
                                                <span className="preview__ticks">
                                                    ✓✓
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="preview__empty">
                                            <div className="preview__emptyIcon">
                                                <svg
                                                    width="23"
                                                    height="23"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.6"
                                                >
                                                    <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
                                                </svg>
                                            </div>

                                            <strong>
                                                No template selected
                                            </strong>

                                            <span>
                                                Select a template to see
                                                how your message will
                                                appear.
                                            </span>
                                        </div>
                                    )}

                                </div>

                                {/* Footer */}
                                <div className="preview__templateFooter">
                                    <div className="preview__templateFooterIcon">
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <circle cx="12" cy="12" r="9" />
                                            <path d="M12 8v4l2.5 2.5" />
                                        </svg>
                                    </div>

                                    <span>
                                        This is a preview.
                                    </span>
                                </div>
                            </section>
                        </div>
                    </div>

            }
            <div className="cf-actions">
                {errors?.contacts && <p className="cf-err">{errors?.contacts}</p>}
                <button type="button" className="cf-btn cf-btn-ghost" onClick={handleBack}>Cancel</button>
                {
                    step > 0 &&
                        <button type="button" className="cf-btn cf-btn-outline" disabled={cursor === 'wait'} onClick={handlePrevious}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 12 19 6 19 18 5 12"/></svg>
                            Previous
                        </button>
                }
                {step < 3 && (
                    <button type="button" className="cf-btn cf-btn-primary" disabled={cursor === 'wait'} onClick={handleNext}>
                        Next
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="19 12 5 18 5 6 19 12"/></svg>
                    </button>
                )}
                {step === 3 && (
                    <button type="button" className="cf-btn cf-btn-primary" disabled={cursor === 'wait'} onClick={handleSubmit}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        Send Campaign
                    </button>
                )}
            </div>
        </div>
    );
};

export default CampaignForm;